interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
  address: {
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
}

interface GeocodingResult {
  latitude: number;
  longitude: number;
  displayAddress: string;
}

const NOMINATIM_API_BASE = 'https://nominatim.openstreetmap.org';
const SEARCH_DELAY = 1000;

let searchTimeout: NodeJS.Timeout | null = null;

export const searchAddresses = async (query: string): Promise<AddressSuggestion[]> => {
  if (!query || query.trim().length < 3) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: '1',
      countrycodes: 'au',
      limit: '5'
    });

    const response = await fetch(`${NOMINATIM_API_BASE}/search?${params}`, {
      headers: {
        'User-Agent': 'SafetyPortalApp/1.0'
      }
    });

    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error searching addresses:', error);
    return [];
  }
};

export const geocodeAddress = async (address: string): Promise<GeocodingResult | null> => {
  try {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      addressdetails: '1',
      countrycodes: 'au',
      limit: '1'
    });

    const response = await fetch(`${NOMINATIM_API_BASE}/search?${params}`, {
      headers: {
        'User-Agent': 'SafetyPortalApp/1.0'
      }
    });

    if (!response.ok) {
      throw new Error('Geocoding service unavailable');
    }

    const results = await response.json();

    if (results && results.length > 0) {
      const result = results[0];
      return {
        latitude: parseFloat(result.lat),
        longitude: parseFloat(result.lon),
        displayAddress: result.display_name
      };
    }

    return null;
  } catch (error) {
    console.error('Error geocoding address:', error);
    return null;
  }
};

export const reverseGeocode = async (latitude: number, longitude: number): Promise<string | null> => {
  try {
    const params = new URLSearchParams({
      lat: latitude.toString(),
      lon: longitude.toString(),
      format: 'json',
      addressdetails: '1'
    });

    const response = await fetch(`${NOMINATIM_API_BASE}/reverse?${params}`, {
      headers: {
        'User-Agent': 'SafetyPortalApp/1.0'
      }
    });

    if (!response.ok) {
      throw new Error('Reverse geocoding service unavailable');
    }

    const result = await response.json();
    return result.display_name || null;
  } catch (error) {
    console.error('Error reverse geocoding:', error);
    return null;
  }
};

export const debouncedSearchAddresses = (
  query: string,
  callback: (results: AddressSuggestion[]) => void
): void => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(async () => {
    const results = await searchAddresses(query);
    callback(results);
  }, SEARCH_DELAY);
};

export const getGoogleMapsLink = (latitude: number, longitude: number): string => {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

export const getOpenStreetMapLink = (latitude: number, longitude: number): string => {
  return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=17`;
};
